import moment from 'moment';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	DateIntervalNavigation,
	FlexGap,
	type ITimeMachine,
	Jumbo,
	Paragraph,
	tokens,
} from '@treams/treams-ui';

import { GroupSelectorNew } from '../goals/group-selector/new/GroupSelectorNew';
import type { GroupSelectionData } from '../goals/group-selector/new/useGroupSelection';
import { AnalyticsMetrics } from './AnalyticsMetrics';
import AnalyticsUserFilterSelector from './AnalyticsUserFilterSelector';

type AnalyticsViewProps = {
	groupSelectionFilter: (group: graphql.Group) => boolean;
	headerButton?: ReactNode;
	headerFooter?: ReactNode;
	timeMachine: ITimeMachine;
	onChangeTimeMachine: (newTimeMachine: ITimeMachine) => void;
	groupSelection: GroupSelectionData;
	loading: boolean;
	engagementOfficerMetricsOnly: boolean;
};

export function AnalyticsView(props: AnalyticsViewProps) {
	const { t } = useTranslation();
	const configuration = useWorkspaceConfiguration();

	const [userFilter, setUserFilter] = useState<graphql.AnalyticsUserFilter | undefined>(
		graphql.AnalyticsUserFilter.ActiveAtEndDate,
	);

	return (
		<>
			<Jumbo
				title={t('analytics.overview.title')}
				footer={props.headerFooter}
				button={props.headerButton}
				wide={true}
			/>
			<Container py={4} wide={true}>
				<Callout
					title={t('analytics.overview.instructions.title')}
					description={t('analytics.overview.instructions.description')}
					color="blue"
					mb={3}
				/>

				<FlexGap alignItems="center" flexWrap="wrap" gap={tokens.gap.list} mb={3}>
					<Paragraph flexGrow={0} flexShrink={0}>
						{t('dashboard.sidebar.filters.departments.title')}:
					</Paragraph>
					{props.loading ? (
						<Button.Skeleton />
					) : (
						<GroupSelectorNew
							canSelect={props.groupSelectionFilter}
							onChange={(newGroupSelection) => {
								props.groupSelection.updateExplicitGroupSelection(newGroupSelection);
							}}
							allGroups={props.groupSelection.allGroups}
							groupSelection={props.groupSelection.groupSelection}
							hasExplicitSelection={props.groupSelection.hasExplicitGroupSelection}
							groupsInSelection={props.groupSelection.groupsInSelection}
						/>
					)}
					<Box flexGrow={1} />
					{configuration.loading && <DateIntervalNavigation.Skeleton />}
					{configuration.data && (
						<DateIntervalNavigation
							value={props.timeMachine}
							onChange={props.onChangeTimeMachine}
							workspaceStartDate={
								configuration.data.startDate
									? moment(configuration.data.startDate).toDate()
									: undefined
							}
						/>
					)}
					<AnalyticsUserFilterSelector onChange={setUserFilter} />
				</FlexGap>

				{!props.loading && (
					<AnalyticsMetrics
						timeMachine={props.timeMachine}
						groups={props.groupSelection.groupsInSelection}
						userFilter={userFilter}
						engagementOfficerMetricsOnly={props.engagementOfficerMetricsOnly}
					/>
				)}
			</Container>
		</>
	);
}
